import React, { useEffect, useRef } from 'react';
import useAxios
 from '../../axios/useAxios';
import { useDispatch, useSelector } from 'react-redux';
import { successPaidOrder } from '../../actions/cartActions';
import { addUsedPromo } from '../../actions/userActions';
import {
  notifyWarning,
  notifyError,
} from '../../utils/Notify';
import { isFullPaymentByJackpot, isTRissuerExcluded } from '../../utils/Validator';
import { useCheckout } from '../../context/checkoutProvider';
import { sentryPspError } from '../../utils/sentry';
import * as Sentry from '@sentry/react';
import {
  CART_PAY_FAIL,
  CART_PAY_REQUEST,
  CART_GOOGLE_PAY_PAYZEN_LOADING,
} from '../../constants/cartConstants';
import { errorMessages, getErrorMessage } from './declinedCbCode';
import { useOrderStatusUpdates } from '../../services/serviceSocket';
import { useOrderLogger } from '../../hooks/useOrderLogger';
import { PaymentProvider, PAYZEN_3DS_ERROR_CODES } from '../paymentConstants';
import { isPayzenPaymentAbandoned } from './payzenErrorCode';
import { handlePaymentLoadFailure, setupPayzenChunkErrorListener } from './payzenLoadErrorHandler';
import { isPhoneValid } from '../../utils/phoneNumber/validation';
import { clearPhoneNumberError, setPhoneNumberError } from '../../actions/phoneActions';
import { calculateTotalToPay } from '../../utils/order/orderUtils';
import { usePaymentErrorHandler } from './hooks/usePaymentErrorHandler';

const PayzenInit = ({
                      onPayzenError,
                      onPaymentLoading,
                      order,
                      promoLoaded,
                      setTRpopup,
                      store,
                    }) => {
  const { dispatchCheckout } = useCheckout();
  const dispatch = useDispatch();
  const axiosInstance = useAxios();
  const user = useSelector((state) => state.user);
  const phoneState = useSelector((state) => state.phone);
  const phoneNumber = phoneState?.phoneNumber;
  const phoneNumberRef = useRef(phoneNumber);
  const { incrementPaymentError, resetPaymentErrors } = usePaymentErrorHandler();


  useEffect(() => {
    phoneNumberRef.current = phoneNumber;
  }, [phoneNumber]);
  const {
    logPaymentInitiated,
    logPaymentWith3DSResult,
    logPaymentFailedWithCard,
    logPaymentMethodChanged,
    logPaymentCancel,
  } = useOrderLogger();

  const checkOrderStatusAndUpdateWhenIsUnpaid = async (orderId) => {
    const response = await axiosInstance.post('/payzen/update-order', {
      orderId: orderId,
    });
  };


  const handleStatusUpdate = (change) => {
    if (change?.status === 'succeeded') {
      const data = {
        message: change?.message,
        status: change?.status,
        order: change?.order,
      };
      dispatchData(data, change?.payzenSavedCard);
    }
  };
  useOrderStatusUpdates(order._id, handleStatusUpdate);

  const dispatchData = (data, payzenSavedCard) => {
    dispatch(successPaidOrder(data));
    dispatch(
      addUsedPromo(
        user,
        axiosInstance,
        order,
        !!payzenSavedCard.pm,
        payzenSavedCard,
      ),
    );
  };

  function closePopupCBAndTR() {
    const buttonCloseCardButtonPayzen =
      document.querySelector('#close-popup-cb');
    if (buttonCloseCardButtonPayzen) {
      buttonCloseCardButtonPayzen.click();
    }
  }

  function is3DSecureUsed(paymentResponse) {
    const transaction = paymentResponse?.clientAnswer?.transactions?.[0];
    const hasStrongAuthenticationEffective = transaction?.effectiveStrongAuthentication === "ENABLED";
    const hasThreeDSResponse = transaction?.transactionDetails?.cardDetails?.threeDSResponse !== null;
    const hasAuthenticationResponse = transaction?.transactionDetails?.cardDetails?.authenticationResponse !== null;

    return hasStrongAuthenticationEffective && (hasThreeDSResponse || hasAuthenticationResponse);
  }

  const getPayzenErrorMessage = (errorMessage, detailedErrorMessage, authorizationResult) => {
    if (authorizationResult) {
      const codeMessage = getErrorMessage(authorizationResult);
      if (codeMessage !== "Code d'erreur inconnu") {
        return codeMessage;
      }
    }
    if (detailedErrorMessage && detailedErrorMessage.length > 0) {
      const customMessage = errorMessages[detailedErrorMessage.toLowerCase()];
      if (customMessage) {
        return customMessage;
      }
      return detailedErrorMessage;
    }
    if (errorMessage && errorMessage.length > 0) {
        return errorMessage;
    }
    return 'Paiement refusé';
};

  const setGooglePayPointerEvents = (status) => {
    if(typeof status !== 'string') {
      return;
    }
    const GpayButton = document.getElementById('GOOGLE_PAY_BUTTON');
    if(GpayButton){
      GpayButton.style.pointerEvents = status;
    }
  }

  let isPaymentSessionActive = false;

  const initializePayzenListeners = async (email, token) => {
    try {
      await window.KR.setFormConfig({ formToken: token, 'kr-language': 'fr-FR' });
      dispatchCheckout({ type: 'PAYZEN_MODULE_LOAD', payload: true });
    } catch (error) {
      console.error('Payzen initialization error:', error);
      dispatchCheckout({ type: 'PAYZEN_MODULE_LOAD', payload: false });
      throw error;
    }
    window.KR.onFormReady(() => {
        if (window.google?.payments) {
        const paymentsClient = new window.google.payments.api.PaymentsClient({
          environment: 'PRODUCTION',
        });

        const isReadyToPayRequest = {
          apiVersion: 2,
          apiVersionMinor: 0,
          allowedPaymentMethods: [{
            type: "CARD",
            parameters: {
              allowedAuthMethods: ["PAN_ONLY", "CRYPTOGRAM_3DS"],
              allowedCardNetworks: ["VISA", "MASTERCARD","AMEX","DISCOVER"]
            }
          }]
        };

        paymentsClient.isReadyToPay(isReadyToPayRequest)
          .then((response) => {
            const timeout = setTimeout(() => {
            dispatchCheckout({
                type: 'PAYZEN_GOOGLE_PAY_RENDER',
                payload: response.result
              });
            }, 2500);
            timeout();
            clearTimeout(timeout);
            console.info('Google Pay is ready:', response.result);
          })
          .catch((err) => {
            console.error("Erreur Google Pay:", err);
            dispatchCheckout({
              type: 'PAYZEN_GOOGLE_PAY_RENDER',
              payload: false
            });
          });
      }
    });
    window.KR.onError((error) => {
      isPaymentSessionActive = false;
      console.info('initializePayzenListeners - window.KR.onError');
      setGooglePayPointerEvents('auto');
      const is3DSError = PAYZEN_3DS_ERROR_CODES.includes(error?.detailedErrorCode);
      const paymentSuccessful = false;

      if(isPayzenPaymentAbandoned(error?.errorCode)) {
        console.info('initializePayzenListeners - window.KR.onError - isPayzenPaymentAbandoned');
        logPaymentCancel(order?._id, order?.userId, {
          errorCode: error?.errorCode,
          errorMessage: error?.errorMessage,
          paymentMethod: error?.paymentMethod,
          paymentProvider: PaymentProvider.PAYZEN,
        });
        if(error?.paymentMethod === 'GOOGLEPAY') {
          closePopupCBAndTR();
        }
        dispatch({ type: CART_PAY_FAIL });
        dispatch({ type: CART_GOOGLE_PAY_PAYZEN_LOADING, payload: false });
        onPaymentLoading(false)
        return
      } else if(error?.paymentMethod === 'APPLE_PAY') {
        logPaymentFailedWithCard(order?._id, order?.userId,{
          errorCode: error?.detailedErrorCode,
          errorMessage: error?.detailedErrorMessage || error?.errorMessage,
          paymentMethod: error?.paymentMethod,
          paymentProvider: PaymentProvider.PAYZEN,
          metadata: error?.metadata,
        });
      }
      else if (error?.paymentMethod === 'GOOGLEPAY') {
        logPaymentFailedWithCard(order?._id, order?.userId,{
          errorCode: error?.detailedErrorCode || error?.errorCode,
          errorMessage: error?.errorMessage || "PAYZEN PAYMENT FAIL",
          paymentMethod: error?.paymentMethod,
          paymentProvider: PaymentProvider.PAYZEN,
          metadata: error?.metadata,
        });
        closePopupCBAndTR();
      }
      else if(error?.paymentMethod === 'CARDS' && !is3DSError) {
        logPaymentFailedWithCard(order?._id, order?.userId,{
          errorCode: error?.detailedErrorCode,
          errorMessage: error?.detailedErrorMessage || error?.errorMessage,
          paymentMethod: error?.paymentMethod,
          paymentProvider: PaymentProvider.PAYZEN,
          metadata: error?.metadata,
        });
      }
      if(is3DSError) {
        logPaymentWith3DSResult(order?._id, order?.userId, paymentSuccessful,{
          errorCode: error?.detailedErrorCode || error?.errorCode,
          errorMessage: error?.errorMessage,
          paymentMethod: error?.paymentMethod,
          paymentProvider: PaymentProvider.PAYZEN,
          metadata: error?.metadata,
        });
      }
      dispatch({ type: CART_PAY_FAIL });
      dispatch({ type: CART_GOOGLE_PAY_PAYZEN_LOADING, payload: false });

      const authorizationResult = error?.metadata?.answer?.clientAnswer?.transactions?.[0]?.transactionDetails?.cardDetails?.authorizationResponse?.authorizationResult;
      const formattedErrorMessage = getPayzenErrorMessage(
        error.errorMessage,
        error.detailedErrorMessage,
        authorizationResult
      );
      const shouldReload = incrementPaymentError(error, formattedErrorMessage);
      if (shouldReload) {
        onPaymentLoading(false);
        return;
      }

      onPaymentLoading(false);
      console.info('WINDOW KR ERROR', error);
      console.info(
        error?.metadata?.answer?.clientAnswer?.transactions?.[0]
          ?.transactionDetails,
      );
      console.info(
        error?.metadata?.answer?.clientAnswer?.transactions?.[0]
          ?.transactionDetails?.cardDetails,
      );
      Sentry.setContext('window kr error', {
        error: error,
        transactionDetails:
          error?.metadata?.answer?.clientAnswer?.transactions?.[0]
            ?.transactionDetails || null,
        cardDetails:
          error?.metadata?.answer?.clientAnswer?.transactions?.[0]
            ?.transactionDetails?.cardDetails || null,
        issuerName:
          error?.metadata?.answer?.clientAnswer?.transactions?.[0]
            ?.transactionDetails?.cardDetails?.issuerName || null,
        detailsErrorMessage: error?.detailedErrorMessage || null,
        errorMessage: error?.errorMessage || null,
      });
      const excludedErrorCodes = ['CLIENT_101'];
      sentryPspError(
        `${error?.errorMessage || 'undefined errorMessage'} errorCode: ${error?.errorCode || 'undefined errorCode'}`,
        'payzen',

        `${
          error?.paymentMethod === 'APPLE_PAY'
            ? 'apple pay'
            : user?.payzenCreditCards?.[0]?.pm?.length > 0
              ? 'cb payzen enregistrée'
              : 'cb payzen non enregistrée'
        }`,
      );
      console.info(
        error?.metadata?.answer?.clientAnswer?.transactions?.[0]
          ?.transactionDetails?.cardDetails?.authorizationResponse
          ?.authorizationResult,
      );
      console.info(
        getErrorMessage(
          error?.metadata?.answer?.clientAnswer?.transactions?.[0]
            ?.transactionDetails?.cardDetails?.authorizationResponse
            ?.authorizationResult,
        ),
      );

      if (error.errorCode && !excludedErrorCodes.includes(error.errorCode)) {
        if (
          error.errorCode === 'CLIENT_309' &&
          error.field === 'cardHolderMail'
        ) {
          onPayzenError('Informations email incorrectes');
        } else if (
          (error?.paymentMethod === 'GOOGLEPAY' || error?.paymentMethod === 'APPLE_PAY') &&
          error?.metadata?.answer?.clientAnswer?.transactions?.[0]
            ?.transactionDetails?.cardDetails?.issuerName &&
          isTRissuerExcluded(
            error?.metadata?.answer?.clientAnswer?.transactions?.[0]
              ?.transactionDetails?.cardDetails?.issuerName,
          )
        ) {
          setTRpopup(true);
        }
        else if (error?.paymentMethod === 'CARDS' && isTRissuerExcluded(
            error?.metadata?.answer?.clientAnswer?.transactions?.[0]
              ?.transactionDetails?.cardDetails?.issuerName,
          )){
          notifyError('Carte non compatible avec la méthode de paiement choisie')
        }
        else if (error?.paymentMethod !== 'GOOGLEPAY') {
          notifyError(getPayzenErrorMessage(error.errorMessage, error.detailedErrorMessage, authorizationResult));
        } else if (error?.paymentMethod !== 'APPLE_PAY') {
          if (
            getErrorMessage(
              error?.metadata?.answer?.clientAnswer?.transactions?.[0]
                ?.transactionDetails?.cardDetails?.authorizationResponse
                ?.authorizationResult,
            ) !== 'Code d\'erreur inconnu'
          ) {
            onPayzenError(
              getErrorMessage(
                error?.metadata?.answer?.clientAnswer?.transactions?.[0]
                  ?.transactionDetails?.cardDetails?.authorizationResponse
                  ?.authorizationResult,
              ),
            );
          } else {
            notifyError(getPayzenErrorMessage(error.errorMessage, error.detailedErrorMessage, authorizationResult));
            onPayzenError(error.detailedErrorMessage);
          }
        } else {
          notifyError(getPayzenErrorMessage(error.errorMessage, error.detailedErrorMessage, authorizationResult));
        }
      } else {
        notifyError(getPayzenErrorMessage(error.errorMessage, error.detailedErrorMessage, authorizationResult));
      }
    });

    window.KR.smartForm.onClick((e) => {
      if (isPaymentSessionActive) {
        console.info('SMART FORM ONCLICK - blocked (session active)');
        return false;
      }
      isPaymentSessionActive = true;

      if(e?.paymentMethod === 'APPLE_PAY' || e?.paymentMethod === 'GOOGLEPAY') {
        const currentPhoneNumber = phoneNumberRef.current;
        const isPhoneValidResult = isPhoneValid(currentPhoneNumber);

        if((!currentPhoneNumber || !isPhoneValidResult) && !user?._id) {
          isPaymentSessionActive = false;
          dispatch(clearPhoneNumberError());
        setTimeout(() => {
          dispatch(setPhoneNumberError());
        }, 0);
          return false;
        }
      }
      console.info('SMART FORM ONCLICK', e)
      logPaymentInitiated(order?._id, order?.userId, e?.paymentMethod, PaymentProvider.PAYZEN);
      dispatch({ type: CART_PAY_REQUEST });
      onPaymentLoading(true);



      if(e?.paymentMethod === 'APPLE_PAY' || e?.paymentMethod === 'GOOGLEPAY') logPaymentMethodChanged(order?._id, order?.userId, e?.paymentMethod);

      if (e && e.paymentMethod === 'GOOGLEPAY') {
        localStorage.removeItem('smartFormClick');
        setGooglePayPointerEvents('none');
        const selectorKrSmartButtonGP = '.kr-smart-button.kr-googlepay[kr-payment-method="googlepay"]';
        const element = document.querySelector(selectorKrSmartButtonGP);
        if (element) {
          localStorage.setItem('smartFormClick', JSON.stringify(e));
        }
        else {
          isPaymentSessionActive = false;
          notifyWarning('Paiement Google Pay', 'Veuillez patientez - chargement en cours')
          return false
        }
      }
    });

    window.KR.onSubmit(async (response) => {
      isPaymentSessionActive = false;
      setGooglePayPointerEvents('auto');
      console.info('KR ONSUBMIT', response);
      const is3DSecureUsedResult = is3DSecureUsed(response);


      if(is3DSecureUsedResult) logPaymentWith3DSResult(order?._id, order?.userId, is3DSecureUsedResult,{
        paymentMethod: response?.clientAnswer?.transactions?.[0]?.paymentMethodType,
        paymentProvider: "CARDS",
        paymentGateway: PaymentProvider.PAYZEN,
        metadata: response?.clientAnswer?.transactions?.[0]
      });

      localStorage.setItem(
        'pendingPayment',
        JSON.stringify({ payment2: 'pending2' }),
      );

      const isPaid = response?.clientAnswer?.orderStatus === 'PAID';

      if (!isPaid || response.clientAnswer.transactions.length === 0) {
        console.error('An error occured during payzen payment');
        return;
      }

      const data = {
        order: { ...order, isPaid: true },
      };
      if (isPaid) {
        await checkOrderStatusAndUpdateWhenIsUnpaid(order._id);
        dispatch(successPaidOrder(data));
      }
    });

    window.KR.onFormValid(() => {
      console.info('initializePayzenListeners - window.KR.onFormValid');
      onPayzenError('');
    });
  };


  const initLib = (token) => {
    console.info('initLib');
    const MAX_ATTEMPTS = 2;
    let attemptCount = 0;

    return new Promise((resolve, reject) => {
      const handleFinalFailure = () => {
        window.isPayzenLoading = false;
        handlePaymentLoadFailure();
      };

      const tryInitializeListeners = async (isFallback = false, currentAttempt) => {
        try {
          await initializePayzenListeners(user?.email, token);
          window.isPayzenLoading = false;
          console.info(`initLib - ${isFallback ? 'fallback ' : ''}onload - initializePayzenListeners`);
          resolve();
        } catch (error) {
          console.error(`${isFallback ? 'Fallback ' : ''}Payzen listeners initialization failed (attempt ${currentAttempt}/${MAX_ATTEMPTS}):`, error);
          handleFinalFailure();
          reject(error);
        }
      };

      const initialize = async () => {
        try {
          if (window.isPayzenLoading) {
            console.info('initLib - window.isPayzenLoading');
            return;
          }
          window.isPayzenLoading = true;

          const existingScript = document.querySelector(
            'script[src="https://static.payzen.eu/static/js/krypton-client/V4.0/stable/kr-payment-form.min.js"]'
          );
          if (existingScript) {
            if (window.KR) {
              console.info('initLib - existingScript - window.KR');
              await tryInitializeListeners(false, 1);
              return;
            }

            console.info('initLib - existingScript - removeChild');
            existingScript.parentNode.removeChild(existingScript);
          }

          // Tentative 1 : Script principal
          attemptCount = 1;
          console.info('initLib - attemptCount', attemptCount);
          const script = document.createElement('script');
          script.src =
            'https://static.payzen.eu/static/js/krypton-client/V4.0/stable/kr-payment-form.min.js';
          script.type = 'text/javascript';
          script.setAttribute('kr-public-key', store.payzenPublicKey);
          script.setAttribute('kr-language', 'fr-FR');

          console.info('initLib - attach Script (attempt 1)');

          script.onload = async (event) => {
            await tryInitializeListeners(false, attemptCount);
        };

        script.onerror = (error) => {
            console.error(`Error loading Payzen script (attempt ${attemptCount}/${MAX_ATTEMPTS}):`, error);

            // Tentative 2 : Fallback script
            attemptCount = 2;
            console.info('initLib - attemptCount', attemptCount);
          const fallbackScript = document.createElement('script');
          fallbackScript.src = 'https://static.payzen.eu/static/js/krypton-client/V4.0/stable/kr-payment-form.min.js';
          fallbackScript.type = 'text/javascript';
          fallbackScript.setAttribute('kr-public-key', store.payzenPublicKey);
          fallbackScript.setAttribute('kr-language', 'fr-FR');

            console.info('initLib - attach Fallback Script (attempt 2)');

          fallbackScript.onload = async (event) => {
              await tryInitializeListeners(true, attemptCount);
          };

          fallbackScript.onerror = (fallbackError) => {
              console.error(`Fallback Payzen script also failed (attempt ${attemptCount}/${MAX_ATTEMPTS}):`, fallbackError);
              handleFinalFailure();
            reject(fallbackError);
          };

          document.body.appendChild(fallbackScript);
        };

          document.body.appendChild(script);
        } catch (error) {
          console.error('initLib - initialize error:', error);
          handleFinalFailure();
          reject(error);
        }
      };
      void initialize();
    });
  };

  const initializePayzen = () => {
    let timerReloadError;
    if(!isFullPaymentByJackpot(order)){
      console.info('initializePayzen');
      axiosInstance
      .post('/payzen/getToken', {
        amount: calculateTotalToPay(order.itemsPrice, order.promoPrice, order?.jackpot?.amount),
        orderId: order._id,
        user,
      })
      .then(async (response) => {
        const token = response.data;
        localStorage.setItem('token', JSON.stringify(token));
        // setPayzenToken(token)
        console.info('initializePayzen response');
        dispatchCheckout({ type: 'PAYZEN_SET_TOKEN', payload: token });
        await initLib(token.paymentToken);
      })
      .catch((error) => {
        console.info('initializePayzen error', error);

        timerReloadError = setTimeout(() => {
          const title = 'Erreur Paiement';
          const description = 'Les options de paiement n\'ont pas charger convenablement. La page va être rechargée.';

          notifyError(title, description);
          window.location.reload();
        }, 5 * 1000);
      });
    }

  };

  useEffect(() => {
    // Configuration du listener d'erreurs de chunks Payzen
    const cleanup = setupPayzenChunkErrorListener(handlePaymentLoadFailure);

    if (promoLoaded) {
      initializePayzen();
    }

    // Cleanup
    return cleanup;
  }, [promoLoaded]);

  return null;
};

export default React.memo(PayzenInit);
